import { createStore, applyMiddleware, combineReducers } from 'redux'
import {
  RouterState,
  connectRouter,
  routerMiddleware,
  RouterLocation,
  replace,
} from 'connected-react-router'
import reduxSaga from 'redux-saga'
import * as effects from 'redux-saga/effects'
import { createBrowserHistory } from 'history'
import { composeWithDevTools } from 'redux-devtools-extension'
import * as locale from './Locale'
import * as balance from './components/Balance'
import * as info from './Info'
import * as home from './components/Home'
import * as wallet from './components/Wallet'
import * as bills from './components/Bills'
import * as payers from './components/Payers'
import * as items from './components/Items'
import * as upload from './components/Upload'
import * as notifications from './components/Notifications'
import * as taxes from './components/TaxRates'
import * as discounts from './components/Discounts'
import {
  prosperusReducer,
  prosperusSaga,
  prosperusInitSaga,
  ProsperusState,
  ProsperusActions,
  NotRequested,
  GetCurrentWorkspaceResultAction,
} from '@library/react-toolkit'
import { pipe } from 'fp-ts/lib/function'
import { io, ioEither, console } from 'fp-ts'
import { getItem } from './io'
import * as localStorage from './LocalStorage'
import { takeLatest } from './saga'

export type Actions =
  | upload.Actions
  | locale.Actions
  | info.Actions
  | balance.Actions
  | payers.Actions
  | items.Actions
  | bills.Actions
  | taxes.Actions
  | discounts.Actions
  | ProsperusActions

function* initSaga() {
  yield effects.all([
    effects.fork(info.infoInit),
    effects.fork(prosperusInitSaga, {
      allowPersonal: false,
      login: process.env.LOGIN,
      password: process.env.PASSWORD,
      activeModules: ['PROSPERBILL'],
    }),
  ])
}

function* reloadOrganizationSaga(a: GetCurrentWorkspaceResultAction) {
  if (a.payload._tag !== 'Ok') {
    return
  }

  const location: RouterLocation<unknown> = yield effects.select(
    (s: State) => s.router.location
  )

  yield effects.put(replace(location))
}

function* rootSaga() {
  yield effects.fork(initSaga)
  yield effects.all([
    localStorage.saga(),
    home.saga(),
    wallet.saga(),
    bills.saga(),
    balance.saga(),
    payers.saga(),
    upload.saga(),
    locale.saga(),
    taxes.saga(),
    discounts.saga(),
    prosperusSaga(),
    takeLatest(
      '@react-toolkit/get_current_workspace_result',
      reloadOrganizationSaga
    ),
  ])
}

const sagaMiddleware = reduxSaga()
export const history = createBrowserHistory({ basename: '/prosper-bill' })

export interface State {
  info: info.State
  locale: locale.State
  router: RouterState
  home: home.State
  wallet: wallet.State
  bills: bills.State
  balance: balance.State
  payers: payers.State
  items: items.State
  upload: upload.State
  notifications: notifications.State
  taxes: taxes.State
  discounts: discounts.State
  prosperus: ProsperusState
}

declare module 'react-redux' {
  interface DefaultRootState extends State {}
}

const loadState: io.IO<Partial<State>> = pipe(
  getItem('info', info.StateC),
  ioEither.map(infoState => ({ info: infoState })),
  ioEither.getOrElse(l =>
    pipe(
      console.warn('bad state'),
      io.chain(() => console.warn(l)),
      io.map((): Partial<State> => ({}))
    )
  )
)

const store = createStore(
  combineReducers<State>({
    info: info.reducer,
    locale: locale.reducer,
    home: home.reducer,
    wallet: wallet.reducer,
    bills: bills.reducer,
    router: connectRouter(history),
    balance: balance.reducer,
    payers: payers.reducer,
    items: items.reducer,
    upload: upload.reducer,
    notifications: notifications.reducer,
    taxes: taxes.reducer,
    discounts: discounts.reducer,
    prosperus: (s, a) =>
      prosperusReducer(
        s ?? {
          units: NotRequested,
          account: NotRequested,
          organization: {
            currentWorkspace: NotRequested,
            selectModalActive: false,
          },
          // @ts-ignore
          dcnNymID: NotRequested,
        },
        a
      ),
  }),
  // @ts-ignore
  loadState(),
  composeWithDevTools({ trace: true })(
    applyMiddleware(sagaMiddleware, routerMiddleware(history))
  )
)

sagaMiddleware.run(rootSaga)

export default store
